import { format, parseISO } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { isBillItemLine, isInvoiceItemLine } from "@common/helpers/lineItems";
import { Bill, BillLineItemBase } from "@common/types/bill";
import { Invoice, InvoiceLineItemBase } from "@common/types/invoice";
import { addTimeZoneOffset } from "@helpers/date";
import { accrualImporterExtractionIdFromPersisted } from "./accrualImporterExtractionId";
import { createAccrualImporterRow } from "./createAccrualImporterRow";
import { createAccrualImporterRowId } from "./createAccrualImporterRowId";
import { AccrualImporterRow } from "./types";

function formatDateOrEmpty(date: Date | string | null | undefined): string {
    if (!date) {
        return "";
    }
    const parsed = typeof date === "string" ? parseISO(date) : date;
    return format(addTimeZoneOffset(parsed), fullDateLongFormat);
}

export function mapBillsToImporterRows(bills: Bill[]): AccrualImporterRow[] {
    const rows: AccrualImporterRow[] = [];

    for (const bill of bills) {
        const lines = (bill.lines ?? []).filter(isBillItemLine);
        if (lines.length === 0) {
            continue;
        }

        for (const line of lines) {
            rows.push(
                createAccrualImporterRow({
                    _id: createAccrualImporterRowId(),
                    extractionId: accrualImporterExtractionIdFromPersisted(
                        bill.id,
                    ),
                    accrualId: bill.id,
                    lineId: line.id,
                    entityId: bill.entityId,
                    counterpartyId: bill.counterparty?.id ?? null,
                    invoiceNumber: bill.invoiceNumber ?? "",
                    memo: bill.memo ?? "",
                    createdDate: formatDateOrEmpty(bill.createdAt),
                    dueDate: formatDateOrEmpty(bill.due),
                    financialAccountId: bill.financialAccountId,
                    totalAmount: bill.total,
                    tax: bill.tax,
                    productOrService: "",
                    catalogItemId: null,
                    catalogItemPriceId: null,
                    description: line.description ?? "",
                    quantity: line.quantityDecimal ?? null,
                    unitPrice: line.unitAmount ?? null,
                    amount: line.amount,
                    accrualLedger: line.accountId ?? "",
                    accrualAccountId: line.accountsPayableId ?? "",
                    classIds: extractClassIds(line),
                    status: bill.status,
                }),
            );
        }
    }

    return rows;
}

export function mapInvoicesToImporterRows(
    invoices: Invoice[],
): AccrualImporterRow[] {
    const rows: AccrualImporterRow[] = [];

    for (const invoice of invoices) {
        const lines = (invoice.lines ?? []).filter(isInvoiceItemLine);
        if (lines.length === 0) {
            continue;
        }

        for (const line of lines) {
            rows.push(
                createAccrualImporterRow({
                    _id: createAccrualImporterRowId(),
                    extractionId: accrualImporterExtractionIdFromPersisted(
                        invoice.id,
                    ),
                    accrualId: invoice.id,
                    lineId: line.id,
                    entityId: invoice.entityId,
                    counterpartyId: invoice.counterparty?.id ?? null,
                    invoiceNumber: invoice.invoiceNumber ?? "",
                    memo: invoice.memo ?? "",
                    createdDate: formatDateOrEmpty(invoice.createdAt),
                    dueDate: formatDateOrEmpty(invoice.due),
                    financialAccountId: invoice.financialAccountId,
                    totalAmount: invoice.total,
                    tax: invoice.tax,
                    productOrService: line.nativeDetail?.productOrService ?? "",
                    catalogItemId: null,
                    catalogItemPriceId: null,
                    description: line.description ?? "",
                    quantity: line.quantityDecimal ?? null,
                    unitPrice: line.unitAmount ?? null,
                    amount: line.amount,
                    accrualLedger: line.accountId ?? "",
                    accrualAccountId: line.accountsReceivableId ?? "",
                    classIds: extractClassIds(line),
                    status: invoice.status,
                }),
            );
        }
    }

    return rows;
}

function extractClassIds(
    line: BillLineItemBase | InvoiceLineItemBase,
): string[] | null {
    const assignments = line.classAssignments;
    if (!assignments?.length) {
        return null;
    }
    return assignments.map((ca) => ca.classId);
}
